import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { AtLeast } from '#layers/base/types/utils';
import type { AnyItem, CompositePrimaryKey, ConnectToMany, DisconnectToMany, FieldFilter, Filter, LogicalFilterOR } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CreateItem } from '~/composables/form-block/use-relation-field-state';
import type { RelationFormFieldContext } from '~/composables/form-block/use-relation-form-field';
import { computed, toValue } from 'vue';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { getRelationConstraint, getRelationPrimitiveFields, isConnectToMany, isCreateToMany, isDisconnectToMany, isEmptyFilter, isUpdateToMany } from '#layers/data-model';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { isSameKey } from '#layers/data-model/utils/primary-key';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/utils/schema/collection';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { getOriginalItemKey, isCreatedItem } from '~/composables/form-block/use-relation-field-state';
import { useDisplayTemplateFieldSelection } from '~/composables/use-display-template-field-selection';
import { useItems } from '~/composables/use-items';
import { buildFilterFromItemKeys } from '~/utils/filters';
import { injectConstraintFields } from '~/utils/inject-constraint-fields';

type RequiredFields = 'field' | 'referencedItemKey' | 'fieldValue';

export interface UseO2MStateOptions extends AtLeast<RelationFormFieldContext<'to-many'>, RequiredFields> {
	pageSize?: MaybeRefOrGetter<number | undefined>;
}

export interface O2MStateHandlers {
	createItem: (item: AnyItem) => void;
	undoCreateItem: (item: CreateItem) => void;
	connectItem: (key: CompositePrimaryKey) => void;
	undoConnectItem: (key: CompositePrimaryKey) => void;
	disconnectItem: (key: CompositePrimaryKey) => void;
	undoDisconnectItem: (key: CompositePrimaryKey) => void;
	updateItem: (originalKey: CompositePrimaryKey | null, item: AnyItem) => void;
}

export interface UseO2MStateReturn extends O2MStateHandlers {
	collection: ComputedRef<ResolvedMonospaceCollection | undefined>;
	items: ComputedRef<AnyItem[]>;
	selectedItemsFilter: ComputedRef<Filter>;
	getItemKey: (item: AnyItem) => CompositePrimaryKey | null;
}

export function useO2MState(options: UseO2MStateOptions) {
	const { referencedItemKey, fieldValue, pageSize } = options;

	const computedField = computed(() => toValue(options.field));
	const constraint = computed(() => computedField.value ? getRelationConstraint(computedField.value).constraint : null);
	const oppositeCollection = computed(() => computedField.value?.oppositeCollection);
	const oppositeCollectionPrimaryKeyFields = computed(() => oppositeCollection.value ? getCollectionPrimaryKeyFields(oppositeCollection.value) : null);

	const connectedItemsFilter = computed<FieldFilter | undefined>(() => {
		const mappedKeys: CompositePrimaryKey = {};
		const referencedItemKeyValue = toValue(referencedItemKey);

		if (!referencedItemKeyValue) {
			return;
		}

		for (const { constrainedField, referencedField } of constraint.value!.fieldMappings) {
			mappedKeys[constrainedField!.apiName] = referencedItemKeyValue![referencedField!.apiName]!;
		}

		return Object.keys(mappedKeys).map((key) => ({ [key]: { _eq: mappedKeys[key] } }))[0];
	});

	function undoConnectItem(itemKey: CompositePrimaryKey) {
		options.emitUndoConnect?.(itemKey);
	}

	function undoDisconnectItem(itemKey: CompositePrimaryKey) {
		options.emitUndoDisconnect?.(itemKey);
	}

	function connectItem(itemKey: CompositePrimaryKey) {
		undoDisconnectItem(itemKey);
		options.emitConnect?.(itemKey);
	}

	function disconnectItem(itemKey: CompositePrimaryKey) {
		undoConnectItem(itemKey);
		options.emitDisconnect?.(itemKey);
	}

	function createItem(item: AnyItem) {
		options.emitCreate?.(item);
	}

	function undoCreateItem(item: CreateItem) {
		options.emitUndoCreate?.(item);
	}

	function updateItem(originalKey: CompositePrimaryKey | null, item: AnyItem) {
		options.emitUpdate?.(originalKey, item);
	}

	function getItemKey(item: AnyItem) {
		return getOriginalItemKey(item) ?? (oppositeCollectionPrimaryKeyFields.value ? pickByPrimitiveFields(item, oppositeCollectionPrimaryKeyFields.value) : null);
	}

	const newlyConnectedKeys = computed(() => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return [];
		}

		return fieldValue.value
			.filter((operation) => isConnectToMany(operation))
			.flatMap((operation: ConnectToMany) => operation._connect.keys);
	});

	const newlyConnectedItemsFilter = computed<Filter>(() => {
		return buildFilterFromItemKeys({
			keys: newlyConnectedKeys.value,
			primaryKeyFields: oppositeCollectionPrimaryKeyFields.value ?? [],
		});
	});

	const newlyDisconnectedKeys = computed(() => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return [];
		}

		return fieldValue.value
			.filter((operation) => isDisconnectToMany(operation))
			.map((operation: DisconnectToMany) => operation._disconnect.filter) as CompositePrimaryKey[];
	});

	const updateOps = computed(() => fieldValue.value?.filter((op) => isUpdateToMany(op)) ?? []);

	const selectedItemsFilter = computed(() => {
		const and: Filter[] = [];
		let orFilter: LogicalFilterOR | null = null;

		function or(filter: Filter) {
			if (orFilter === null) {
				orFilter = { _or: [] };
				and.push(orFilter);
			}

			orFilter._or.push(filter);
		}

		if (connectedItemsFilter.value && !isEmptyFilter(connectedItemsFilter.value)) {
			or(connectedItemsFilter.value);
		}

		if (!isEmptyFilter(newlyConnectedItemsFilter.value)) {
			or(newlyConnectedItemsFilter.value);
		}

		return {
			_and: and,
		};
	});

	const filterEmpty = computed(() => isEmptyFilter(selectedItemsFilter.value));

	const displayTemplateFieldSelection = useDisplayTemplateFieldSelection(oppositeCollection);

	const fieldSelection = computed(() => {
		const field = computedField.value;
		if (!field) return displayTemplateFieldSelection.value;
		return injectConstraintFields(displayTemplateFieldSelection.value, getRelationPrimitiveFields(field).oppositeFields);
	});

	const remoteItems = useItems({
		collection: oppositeCollection,
		enabled: () => !!computedField.value && !filterEmpty.value,
		query: () => ({
			filter: selectedItemsFilter.value,
			fields: fieldSelection.value,
		}),
		pageSize,
	});

	const createdItems = computed(() => !isNullishOrEmpty(fieldValue.value)
		? fieldValue.value
				.filter((op) => isCreateToMany(op))
				.flatMap((op) => op._create.data)
		: []);

	const items = computed(() => {
		if (remoteItems.pageNumber.value === 1) {
			return [
				...remoteItems.items.value,
				...createdItems.value,
			];
		}
		else {
			return remoteItems.items.value;
		}
	});

	const itemsWithUpdateEdits = computed(() => items.value.map((item) => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return item;
		}

		const itemEdits = updateOps.value.find((op) => isSameKey(op._update.filter as CompositePrimaryKey, getItemKey(item)));

		if (itemEdits && isUpdateToMany(itemEdits)) {
			return { ...item, ...itemEdits._update.data };
		}

		return item;
	}));

	const totalItemsCount = computed(() => {
		if (remoteItems.totalItemsCount.value != null) {
			return remoteItems.totalItemsCount.value + createdItems.value.length;
		}
	});

	const currentItemsCount = computed(() => remoteItems.allItems.value.length + createdItems.value.length);

	// Autorefresh on submit
	// The engine doesn't return the results
	const formBlockContext = injectFormBlockContext(null);

	if (formBlockContext) {
		formBlockContext.state.value?.$onSuccess?.(() => {
			remoteItems.refresh();
		});
	}

	// eslint-disable-next-line unicorn/consistent-function-scoping
	function isNewlyCreated(item: object): boolean {
		return isCreatedItem(item);
	}

	function isNewlyUpdated(item: object): boolean {
		const key = getItemKey(item);
		return key && updateOps.value.some((updateOp) => isSameKey(updateOp._update.filter as CompositePrimaryKey, key));
	}

	function isNewlyConnected(item: object): boolean {
		const key = getItemKey(item);
		return key && newlyConnectedKeys.value.some((connectedKey) => isSameKey(connectedKey, key));
	}

	function isNewlyDisconnected(item: object): boolean {
		const key = getItemKey(item);
		return key && newlyDisconnectedKeys.value.some((disconnectedKey) => isSameKey(disconnectedKey, key));
	}

	return {
		collection: oppositeCollection,
		items: itemsWithUpdateEdits,
		pageNumber: remoteItems.pageNumber,
		totalItemsCount,
		currentItemsCount,
		isLoading: remoteItems.isLoading,
		createItem,
		undoCreateItem,
		connectItem,
		undoConnectItem,
		disconnectItem,
		undoDisconnectItem,
		updateItem,
		selectedItemsFilter,
		getItemKey,
		isNewlyCreated,
		isNewlyUpdated,
		isNewlyConnected,
		isNewlyDisconnected,
	};
}
